import type { Except } from 'type-fest';
import type {
    ChatMessage,
    Feature
} from '@public-api-contracts/models/chat/ChatMessage';
import type { ProductSearchComplementaryCategoryFeatureOptions } from '../../../api/models/FeatureOptions';
import { AppliedFilters } from '../../../models/AppliedFilters';
import type { ProductSearchComplementaryCategoryFeatureItem } from '../../../models/features/main/ProductSearchComplementaryCategoryFeatureItem';
import type { AppDispatch, RootState } from '../../../store/store';
import { apiClient } from '../../ApiClient';
import {
    defaultBackToPreviousFeatureItems,
    getDefaultBackableFeatureItem,
    getDefaultFilterableFeatureItem,
    getDefaultWithSuggestionsFeatureItem,
    getNewFeatureUuid
} from '../../FeatureService';
import type { FeatureHandler } from '../FeatureHandler';
import { dispatchEvent } from '../../EventService';
import {
    transformFieldFiltersToTaxonomyTags,
    transformCategoryFiltersToTaxonomyTags
} from '../../FilterService';

export const productSearchComplementaryCategoryMainFeatureHandler = {
    async startFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        featureOptions: ProductSearchComplementaryCategoryFeatureOptions
    ): Promise<void> {
        const shouldOpenSidebar = !state.featureState.mainFeatureItem;
        const newMainFeatureItem: ProductSearchComplementaryCategoryFeatureItem =
            {
                featureUuid: getNewFeatureUuid(
                    'product-search-complementary-category'
                ),
                featureType: 'product-search-complementary-category',
                combinationWithProductIds: [
                    ...featureOptions.combinationWithProductIds
                ],
                combinationWithProducts: [],
                totalQuantity: 0,
                loadingStatus: 'loading',
                products: [],
                categoryFilter: featureOptions.categoryFilter,
                ...getDefaultFilterableFeatureItem(),
                ...getDefaultWithSuggestionsFeatureItem(),
                ...getDefaultBackableFeatureItem(state)
            };
        dispatch.featureState.setMenuFeatureItem(null);
        dispatch.featureState.setMainFeatureItem(newMainFeatureItem);
        dispatch.uiState.setMode('full');

        await dispatch.featureState.startFeature({
            featureType: 'chat',
            chatType: 'general'
        });
        const feature: Feature = {
            featureType: 'product-search-complementary-category',
            request: {
                productIds: featureOptions.combinationWithProductIds,
                pageSize: 24,
                categoryFilter: featureOptions.categoryFilter,
                sortBy: 'relevance'
            }
        };
        const newPartialChatMessage = {
            role: 'assistant',
            content: {
                contentType: 'feature',
                startedBy: 'user-from-ui',
                status: 'loading',
                feature
            }
        } as const satisfies Except<ChatMessage, 'timestamp' | 'uuid'>;
        const newChatMessage = dispatch.featureState.appendNewChatMessage(
            newPartialChatMessage
        );
        dispatchEvent({
            type: 'bri.start_search',
            isInteraction: true,
            source: 'brilliant',
            brilliantSource: 'ui',
            searchType: 'product-search-complementary-category',
            categoryValue: featureOptions.categoryFilter.categoryValue
        });
        try {
            const {
                jsonResult: {
                    combinationWithProducts,
                    products,
                    suggestedProducts,
                    suggestedFieldFilters,
                    suggestedCategoryFilters,
                    availableFieldFilters,
                    availableCategoryFilters,
                    totalQuantity,
                    maxPrice,
                    minPrice
                }
            } = await apiClient.product.productSearchComplementaryCategory({
                body: {
                    productIds: featureOptions.combinationWithProductIds,
                    pageSize: 24,
                    categoryFilter: featureOptions.categoryFilter,
                    sortBy: 'relevance'
                }
            });
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                loadingStatus: 'success',
                combinationWithProducts,
                suggestedFieldFilters,
                suggestedCategoryFilters,
                availableFieldFilters,
                availableCategoryFilters,
                products,
                suggestedProducts,
                totalQuantity,
                availableMaxPrice: maxPrice ?? null,
                availableMinPrice: minPrice ?? null
            });

            dispatchEvent({
                type: 'bri.open_search_result',
                isInteraction: true,
                source: 'brilliant',
                brilliantSource: 'ui',
                searchType: 'product-search-complementary-category',
                categoryValue: featureOptions.categoryFilter.categoryValue,
                productIds: products.map(p => p.productId)
            });
            if (newChatMessage) {
                dispatch.featureState.patchExistingChatMessage({
                    uuid: newChatMessage.uuid,
                    content: {
                        ...(newChatMessage.content as (typeof newPartialChatMessage)['content']),
                        status: 'success',
                        feature: {
                            ...feature,
                            result: {
                                combinationWithProducts,
                                products,
                                suggestedProducts,
                                suggestedFieldFilters,
                                suggestedCategoryFilters,
                                availableFieldFilters,
                                availableCategoryFilters,
                                totalQuantity,
                                maxPrice,
                                minPrice
                            }
                        }
                    }
                });
            }
            if (shouldOpenSidebar) {
                dispatch.featureState.setSidebarExpanded(true);
            }
            void dispatch.featureState.processMessages({
                handleLoading: true
            });
        } catch {
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                loadingStatus: 'error'
            });
            if (newChatMessage) {
                dispatch.featureState.patchExistingChatMessage({
                    uuid: newChatMessage.uuid,
                    content: {
                        ...(newChatMessage.content as (typeof newPartialChatMessage)['content']),
                        status: 'error'
                    }
                });
            }
        }
    },
    restoreFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        feature: Extract<
            Feature,
            { featureType: 'product-search-complementary-category' }
        >
    ): void {
        const restoredFeatureItem: ProductSearchComplementaryCategoryFeatureItem =
            {
                ...getDefaultFilterableFeatureItem(),
                ...getDefaultWithSuggestionsFeatureItem(),
                ...getDefaultBackableFeatureItem(state),
                featureType: 'product-search-complementary-category',
                combinationWithProductIds: [
                    ...(feature.request.productIds ?? [])
                ],
                combinationWithProducts:
                    feature.result?.combinationWithProducts ?? [],
                categoryFilter: feature.request.categoryFilter,
                products: feature.result?.products ?? [],
                totalQuantity: feature.result?.totalQuantity ?? 0,
                suggestedProducts: feature.result?.suggestedProducts ?? [],
                suggestedFieldFilters:
                    feature.result?.suggestedFieldFilters ?? [],
                suggestedCategoryFilters:
                    feature.result?.suggestedCategoryFilters ?? [],
                availableFieldFilters:
                    feature.result?.availableFieldFilters ?? [],
                availableCategoryFilters:
                    feature.result?.availableCategoryFilters ?? [],
                availableMaxPrice: feature.result?.maxPrice ?? null,
                availableMinPrice: feature.result?.minPrice ?? null,
                featureUuid: getNewFeatureUuid(
                    'product-search-complementary-category'
                ),
                loadingStatus: 'success',
                refinementLoadingStatus: 'idle'
            };
        dispatch.featureState.setMainFeatureItem(restoredFeatureItem);
    },
    isFilterable: true,
    async applyFilters(
        dispatch: AppDispatch,
        state: RootState,
        appliedFilters: Partial<AppliedFilters>
    ): Promise<void> {
        const featureItem = state.featureState.mainFeatureItem;
        if (
            featureItem?.featureType !== 'product-search-complementary-category'
        ) {
            return;
        }
        const newFeatureItem: ProductSearchComplementaryCategoryFeatureItem = {
            ...featureItem,
            refinementLoadingStatus: 'loading',
            ...appliedFilters
        };
        dispatch.featureState.updateMainFeatureItem(newFeatureItem);
        try {
            const {
                appliedFieldFilters,
                appliedCategoryFilters,
                appliedMaxPrice,
                appliedMinPrice,
                appliedSortBy
            } = newFeatureItem;

            dispatchEvent({
                type: 'bri.change_search_result_filter',
                isInteraction: true,
                source: 'brilliant',
                brilliantSource: 'ui',
                searchType: 'product-search-complementary-category',
                maxPrice: appliedMaxPrice,
                minPrice: appliedMinPrice,
                sortBy: appliedSortBy,
                taxonomyTags: [
                    ...transformFieldFiltersToTaxonomyTags(appliedFieldFilters),
                    ...transformCategoryFiltersToTaxonomyTags(
                        appliedCategoryFilters
                    )
                ]
            });
            const {
                jsonResult: { products, totalQuantity }
            } =
                await apiClient.product.productSearchComplementaryCategoryRefinement(
                    {
                        body: {
                            pageSize: 24,
                            productIds:
                                newFeatureItem.combinationWithProductIds,
                            categoryValue:
                                newFeatureItem.categoryFilter.categoryValue,
                            appliedFieldFilters,
                            maxPrice: appliedMaxPrice ?? undefined,
                            minPrice: appliedMinPrice ?? undefined,
                            sortBy: appliedSortBy
                        }
                    }
                );
            dispatch.featureState.updateMainFeatureItem({
                ...newFeatureItem,
                refinementLoadingStatus: 'success',
                products,
                totalQuantity
            });
        } catch {
            dispatch.featureState.updateMainFeatureItem({
                ...newFeatureItem,
                refinementLoadingStatus: 'error'
            });
        }
    },
    isBackable: true,
    backToPreviousFeatureItems(dispatch: AppDispatch, state: RootState): void {
        defaultBackToPreviousFeatureItems(dispatch, state);
    }
} as const satisfies FeatureHandler<ProductSearchComplementaryCategoryFeatureItem>;
